import {
  isAlertAnswer,
  isAnswered,
  parseAlertValues,
  parseStationAnswerKey,
  stationAnswerKey,
  stationState,
  tallyStations,
  type StationState,
} from '@fieldops/shared'
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { cn } from '../../lib/cn.js'
import { formatDate } from '../../lib/format.js'
import { Icon } from '../Icon.js'
import { Button, Input } from '../ui.js'
import { Choice, PhotoGrid } from './FieldCard.js'
import { asText, optionsOf, shapeOf, toggleChoice } from './model.js'
import type {
  ReportAnswer,
  ReportAnswers,
  ReportField,
  ReportPhoto,
  ReportStation,
  ReportStationHistory,
  ReportStep,
} from './types.js'

/**
 * A station section: the same few questions, once per station.
 *
 * ServiceM8 made each station its own form, opened one at a time from an
 * asset list — 5,556 rat-station checks in the import, four taps each and a
 * screen change between every one. Here a station is a card, its questions are
 * chips on the card, and the whole site is one scroll: the order the stations
 * are listed is the order the technician walks them.
 *
 * Each card says when the station was last checked and what was found, because
 * "R4 had rodents last month" is exactly what a technician wants to know when
 * they lift R4's lid. It is shown and never filled in: a station check is a
 * finding, and findings never carry (decision 56).
 *
 * **One station is open at a time.** Fifty open cards is a page nobody can
 * read back, so every other station is one line: its name, where it is, what
 * was found today (or last time, before it is checked) and its badge. The
 * first station still to do opens on arrival. Answering its last question
 * closes it and opens the next one still to do — unless it needs attention,
 * where a photo usually follows, or the answer was a multi-choice, where a
 * second option might; then Next station moves on. Any line opens on a tap, and
 * a jump from the check sheet opens the station it names.
 *
 * The stations belong to the site, so a station found on site that is not on
 * the list is added here and is on the list next visit. ADR 029.
 */

export type StationActions = {
  /** The suggested name for a new station of this section's type. */
  suggestName: (sectionKey: string) => Promise<string>
  add: (sectionKey: string, name: string, location: string | null) => Promise<void>
  update: (assetId: string, patch: { name?: string; location?: string | null; retired?: boolean }) => Promise<void>
}

export function StationStep({
  step,
  stations,
  history,
  answers,
  photosByField,
  blanksOnly,
  readOnly,
  onChange,
  capture,
  actions,
  jumpTo = null,
}: {
  step: ReportStep
  stations: ReportStation[]
  history: ReportStationHistory
  answers: ReportAnswers
  photosByField: Record<string, ReportPhoto[]>
  blanksOnly: boolean
  readOnly: boolean
  onChange: (key: string, value: ReportAnswer) => void
  /** A station answer key the check sheet jumped to: that station opens. */
  jumpTo?: string | null
  capture?: {
    addPhoto: (key: string) => void
    removePhoto: (photoId: string) => void
    captionPhoto: (photoId: string, caption: string) => void
  }
  actions?: StationActions
}) {
  const questions = step.fields.filter((field) => field.type !== 'HEADING')
  const specs = questions.map((field) => ({
    key: field.key,
    label: field.label,
    type: field.type,
    alertValues: field.alertValues,
  }))
  const refs = stations.map((station) => ({ id: station.id, name: station.name }))
  const tally = tallyStations(specs, refs, answers)
  const typeName = step.stationType?.name.toLowerCase() ?? 'station'

  const shown = blanksOnly
    ? stations.filter((station) => {
        const state = stationState(specs, answers, station.id)
        return state === 'unchecked' || state === 'partial'
      })
    : stations

  // Done means every question but a photo has an answer. A station with
  // activity and a blank beside it is not done, whatever its badge says.
  const required = questions.filter((field) => field.type !== 'PHOTO')
  const isDone = (stationId: string, against: ReportAnswers) =>
    required.length > 0 &&
    required.every((field) => isAnswered(against[stationAnswerKey(field.key, stationId)]))
  const nextToDo = (after: string | null, against: ReportAnswers) => {
    const start = after === null ? 0 : stations.findIndex((station) => station.id === after) + 1
    const order = [...stations.slice(start), ...stations.slice(0, start)]
    return order.find((station) => station.id !== after && !isDone(station.id, against))?.id ?? null
  }

  const [openId, setOpenId] = useState<string | null>(() =>
    readOnly ? null : nextToDo(null, answers),
  )

  // A jump from the check sheet opens its station and brings the field into
  // view once the card has drawn its questions.
  const [scrollTo, setScrollTo] = useState<string | null>(null)
  useEffect(() => {
    const target = jumpTo ? parseStationAnswerKey(jumpTo) : null
    if (target && stations.some((station) => station.id === target.assetId)) {
      setOpenId(target.assetId)
      setScrollTo(jumpTo)
    }
  }, [jumpTo, stations])
  const list = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (scrollTo === null) return
    const field = list.current?.querySelector<HTMLElement>(`[data-field="${scrollTo}"]`)
    field?.scrollIntoView({ block: 'center' })
    field?.querySelector<HTMLElement>('button, input')?.focus()
    setScrollTo(null)
  }, [scrollTo, openId])

  // Moving on brings the next station's name to the top of the view, so the
  // technician reads which lid to lift before its questions.
  const [reveal, setReveal] = useState<string | null>(null)
  useEffect(() => {
    if (reveal === null) return
    list.current
      ?.querySelector<HTMLElement>(`[data-station="${reveal}"]`)
      ?.scrollIntoView?.({ block: 'start', behavior: 'smooth' })
    setReveal(null)
  }, [reveal])
  const moveTo = (stationId: string | null) => {
    setOpenId(stationId)
    setReveal(stationId)
  }

  const answer = (stationId: string, field: ReportField, key: string, value: ReportAnswer) => {
    onChange(key, value)
    const next = { ...answers, [key]: value }
    const finished = !isDone(stationId, answers) && isDone(stationId, next)
    if (!finished) return
    // Stay open where the next thing is probably on this card: a photo of
    // the activity, or a second option on a multi-choice. "None" on its own
    // has nothing to add to it.
    if (stationState(specs, next, stationId) === 'alert') return
    if (field.type === 'MULTI_SELECT' && !(Array.isArray(value) && value.length === 1 && NOTHING.test(value[0]!))) return
    moveTo(nextToDo(stationId, next))
  }

  return (
    <div className="flex flex-col gap-2.5">
      <div
        role="status"
        className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-[12px] bg-white px-3.5 py-2.5 text-[13px] ring-1 ring-ink-200 ring-inset"
      >
        <span className="font-semibold text-ink-900">
          {tally.total} {tally.total === 1 ? 'station' : 'stations'}
        </span>
        <span className="text-ink-600">{tally.checked} checked</span>
        {tally.alert > 0 ? (
          <span className="font-semibold text-red-700">
            {tally.alert} {tally.alert === 1 ? 'needs' : 'need'} attention
          </span>
        ) : null}
        {tally.unchecked.length > 0 ? (
          <span className="text-ink-500">{tally.unchecked.length} to go</span>
        ) : null}
      </div>

      {stations.length === 0 ? (
        <div className="rounded-[14px] bg-white px-[var(--card-pad)] py-6 text-center ring-1 ring-ink-200 ring-inset">
          <p className="text-sm font-semibold">No {typeName}s at this property yet.</p>
          <p className="mt-1 text-[13px] text-ink-500">
            Add each one as you find it. They stay on the property for the next visit.
          </p>
        </div>
      ) : null}

      <div ref={list} className="flex flex-col gap-1.5">
        {shown.map((station) => {
          const next = nextToDo(station.id, answers)
          return (
            <StationCard
              key={station.id}
              station={station}
              questions={questions}
              state={stationState(specs, answers, station.id)}
              last={history[station.id] ?? null}
              answers={answers}
              photosByField={photosByField}
              readOnly={readOnly}
              open={openId === station.id}
              done={isDone(station.id, answers)}
              nextName={next ? (stations.find((candidate) => candidate.id === next)?.name ?? null) : null}
              onToggle={() => setOpenId((current) => (current === station.id ? null : station.id))}
              onNext={() => moveTo(next)}
              onChange={(field, key, value) => answer(station.id, field, key, value)}
              {...(capture ? { capture } : {})}
              {...(actions ? { onUpdate: (patch) => actions.update(station.id, patch) } : {})}
            />
          )
        })}
      </div>

      {blanksOnly && shown.length === 0 && stations.length > 0 ? (
        <div className="rounded-[14px] bg-white px-[var(--card-pad)] py-6 text-center ring-1 ring-ink-200 ring-inset">
          <p className="text-sm font-semibold">Every station is checked.</p>
          <p className="mt-1 text-[13px] text-ink-500">Turn off the blanks filter to read them back.</p>
        </div>
      ) : null}

      {actions && !readOnly && step.sectionKey ? (
        <AddStation
          sectionKey={step.sectionKey}
          typeName={typeName}
          actions={actions}
        />
      ) : null}
    </div>
  )
}

/** A multi-choice answer that leaves nothing else to tick. */
const NOTHING = /^(none|nothing|not needed|no activity)$/i

const STATE_LABEL: Record<StationState, { label: string; className: string }> = {
  unchecked: { label: 'Not checked', className: 'bg-ink-100 text-ink-500' },
  partial: { label: 'Part done', className: 'bg-amber-50 text-amber-800' },
  clear: { label: 'Checked', className: 'bg-brand-100 text-brand-800' },
  alert: { label: 'Needs attention', className: 'bg-red-50 text-red-700' },
}

/** What was answered on one station, in question order, with activity marked. */
function answeredLine(questions: ReportField[], source: Record<string, ReportAnswer>, keyOf: (key: string) => string) {
  return questions
    .filter((field) => field.type !== 'PHOTO' && isAnswered(source[keyOf(field.key)]))
    .map((field) => ({
      key: field.key,
      text: asText(source[keyOf(field.key)] ?? null),
      alert: isAlertAnswer(source[keyOf(field.key)] ?? null, parseAlertValues(field.alertValues)),
    }))
}

function Findings({ parts }: { parts: Array<{ key: string; text: string; alert: boolean }> }) {
  return (
    <>
      {parts.map((part, index) => (
        <span key={part.key}>
          {index > 0 ? ' · ' : null}
          <span className={cn('font-medium', part.alert ? 'text-red-700' : 'text-ink-800')}>{part.text}</span>
        </span>
      ))}
    </>
  )
}

function StationCard({
  station,
  questions,
  state,
  last,
  answers,
  photosByField,
  readOnly,
  open,
  done,
  nextName,
  onToggle,
  onNext,
  onChange,
  capture,
  onUpdate,
}: {
  station: ReportStation
  questions: ReportField[]
  state: StationState
  last: ReportStationHistory[string] | null
  answers: ReportAnswers
  photosByField: Record<string, ReportPhoto[]>
  readOnly: boolean
  open: boolean
  done: boolean
  /** The next station still to do, for the button at the foot of an open card. */
  nextName: string | null
  onToggle: () => void
  onNext: () => void
  onChange: (field: ReportField, key: string, value: ReportAnswer) => void
  capture?: {
    addPhoto: (key: string) => void
    removePhoto: (photoId: string) => void
    captionPhoto: (photoId: string, caption: string) => void
  }
  onUpdate?: (patch: { name?: string; location?: string | null; retired?: boolean }) => Promise<void>
}) {
  const [editing, setEditing] = useState(false)
  const badge = STATE_LABEL[state]

  // What was found last time, in the order the questions are asked.
  // Activity found then is red, the way it printed on the last report.
  const lastLine = last ? answeredLine(questions, last.answers, (key) => key) : []
  const todayLine = answeredLine(questions, answers, (key) => stationAnswerKey(key, station.id))

  if (!open) {
    return (
      <section
        aria-label={`Station ${station.name}`}
        data-station={station.id}
        className={cn(
          'scroll-mt-2 rounded-[12px] bg-white ring-1 ring-inset',
          state === 'alert' ? 'ring-red-300' : 'ring-ink-200',
        )}
      >
        <button
          type="button"
          aria-expanded={false}
          onClick={onToggle}
          className="flex w-full items-center gap-3 rounded-[12px] px-[var(--card-pad)] py-2.5 text-left transition-colors hover:bg-ink-50"
        >
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="flex min-w-0 items-baseline gap-2">
              <span className="shrink-0 text-[18px] leading-tight font-bold text-ink-900">{station.name}</span>
              {station.location ? (
                <span className="truncate text-[13px] text-ink-500">{station.location}</span>
              ) : null}
            </span>
            {todayLine.length > 0 ? (
              <span className="truncate text-[12.5px] text-ink-600">
                <Findings parts={todayLine} />
              </span>
            ) : lastLine.length > 0 ? (
              <span className="truncate text-[12px] text-ink-500">
                Last time: <Findings parts={lastLine} />
              </span>
            ) : null}
          </span>
          <span
            className={cn(
              'inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold',
              badge.className,
            )}
          >
            {state === 'clear' && done ? <Icon name="check" className="size-3" strokeWidth={3} /> : null}
            {badge.label}
          </span>
          <Icon name="chevronDown" className="size-4 shrink-0 text-ink-400" />
        </button>
      </section>
    )
  }

  return (
    <section
      aria-label={`Station ${station.name}`}
      data-station={station.id}
      className={cn(
        'flex scroll-mt-2 flex-col gap-3 rounded-[14px] bg-white p-[var(--card-pad)] ring-1 ring-inset',
        state === 'alert' ? 'ring-red-300' : 'ring-brand-300',
      )}
    >
      <header className="flex items-start gap-3">
        <button
          type="button"
          aria-expanded
          onClick={onToggle}
          className="min-w-0 flex-1 text-left"
        >
          <h3 className="text-[22px] leading-tight font-bold text-ink-900">
            {station.name}
            {station.retiredAt ? (
              <span className="ml-2 text-[11px] font-medium text-ink-500">retired</span>
            ) : null}
          </h3>
          {station.location ? <p className="text-[13.5px] text-ink-500">{station.location}</p> : null}
        </button>
        <span className={cn('shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold', badge.className)}>
          {badge.label}
        </span>
        {onUpdate && !readOnly ? (
          <button
            type="button"
            aria-label={`Edit station ${station.name}`}
            onClick={() => setEditing((on) => !on)}
            className="grid size-8 shrink-0 place-items-center rounded-md text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-900"
          >
            <Icon name="pencil" className="size-4" />
          </button>
        ) : null}
      </header>

      {editing && onUpdate ? (
        <EditStation station={station} onUpdate={onUpdate} onDone={() => setEditing(false)} />
      ) : null}

      {last ? (
        <p className="flex items-start gap-1.5 text-[12px] leading-snug text-ink-500">
          <Icon name="refresh" className="mt-px size-3.5 shrink-0" />
          <span>
            Last checked {formatDate(last.at)}
            {last.by ? ` by ${last.by}` : ''}
            {lastLine.length > 0 ? ': ' : null}
            <Findings parts={lastLine} />
          </span>
        </p>
      ) : null}

      {questions.map((field) => {
        const key = stationAnswerKey(field.key, station.id)
        return (
          <StationQuestion
            key={field.id}
            field={field}
            answerKey={key}
            answer={answers[key]}
            photos={photosByField[key] ?? []}
            readOnly={readOnly}
            onChange={(value) => onChange(field, key, value)}
            {...(capture
              ? {
                  onAddPhoto: () => capture.addPhoto(key),
                  onRemovePhoto: capture.removePhoto,
                  onCaptionPhoto: capture.captionPhoto,
                }
              : {})}
          />
        )
      })}

      {readOnly ? null : (
        <div className="flex justify-end pt-1">
          <Button variant={done ? 'primary' : 'secondary'} onClick={onNext}>
            {nextName ? `Next station: ${nextName}` : done ? 'Done' : 'Close'}
          </Button>
        </div>
      )}
    </section>
  )
}

/** One question on a station card: a label and its chips, compact. */
function StationQuestion({
  field,
  answerKey,
  answer,
  photos,
  readOnly,
  onChange,
  onAddPhoto,
  onRemovePhoto,
  onCaptionPhoto,
}: {
  field: ReportField
  answerKey: string
  answer: ReportAnswer
  photos: ReportPhoto[]
  readOnly: boolean
  onChange: (value: ReportAnswer) => void
  onAddPhoto?: () => void
  onRemovePhoto?: (photoId: string) => void
  onCaptionPhoto?: (photoId: string, caption: string) => void
}) {
  const shape = shapeOf(field)
  const options = optionsOf(field)

  return (
    <div data-field={answerKey} className="flex flex-col gap-1.5">
      <p className="text-[11.5px] font-semibold tracking-[0.025em] text-ink-500 uppercase">{field.label}</p>

      {shape === 'chips' || shape === 'dropdown' ? (
        <div role="group" aria-label={field.label} className="flex flex-wrap gap-1.5">
          {options.map((option) => (
            <Choice
              key={option}
              selected={Array.isArray(answer) ? answer.includes(option) : answer === option}
              disabled={readOnly}
              onClick={() => onChange(toggleChoice(field, answer, option))}
            >
              {option}
            </Choice>
          ))}
        </div>
      ) : null}

      {shape === 'number' ? (
        <Input
          aria-label={field.label}
          type="number"
          inputMode="decimal"
          disabled={readOnly}
          value={typeof answer === 'number' ? String(answer) : ''}
          onChange={(event) => onChange(event.target.value === '' ? null : Number(event.target.value))}
          className="max-w-[160px]"
        />
      ) : null}

      {shape === 'text' || shape === 'textarea' ? (
        <Input
          aria-label={field.label}
          disabled={readOnly}
          value={typeof answer === 'string' ? answer : ''}
          onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
        />
      ) : null}

      {shape === 'photo' ? (
        <PhotoGrid
          label={field.label}
          photos={photos}
          readOnly={readOnly}
          compact
          {...(onAddPhoto ? { onAdd: onAddPhoto } : {})}
          {...(onRemovePhoto ? { onRemove: onRemovePhoto } : {})}
          {...(onCaptionPhoto ? { onCaption: onCaptionPhoto } : {})}
        />
      ) : null}

    </div>
  )
}

function EditStation({
  station,
  onUpdate,
  onDone,
}: {
  station: ReportStation
  onUpdate: (patch: { name?: string; location?: string | null; retired?: boolean }) => Promise<void>
  onDone: () => void
}) {
  const [name, setName] = useState(station.name)
  const [location, setLocation] = useState(station.location ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const run = async (patch: { name?: string; location?: string | null; retired?: boolean }) => {
    setSaving(true)
    setError(null)
    try {
      await onUpdate(patch)
      onDone()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That did not save.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form
      className="flex flex-col gap-2 rounded-[12px] bg-ink-50 p-3 ring-1 ring-ink-200 ring-inset"
      onSubmit={(event: FormEvent) => {
        event.preventDefault()
        void run({ name: name.trim() || station.name, location: location.trim() || null })
      }}
    >
      <div className="grid gap-2 sm:grid-cols-[120px_1fr]">
        <Input aria-label="Station name" value={name} onChange={(event) => setName(event.target.value)} />
        <Input
          aria-label="Where it is"
          placeholder="Where it is, for somebody who has never been here"
          value={location}
          onChange={(event) => setLocation(event.target.value)}
        />
      </div>
      {error ? <p className="text-xs text-red-700">{error}</p> : null}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" size="sm" variant="primary" loading={saving}>
          Save
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        {station.retiredAt === null ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="ml-auto text-red-700"
            onClick={() => void run({ retired: true })}
          >
            Retire this station
          </Button>
        ) : null}
      </div>
      <p className="text-[11.5px] text-ink-500">
        Retiring takes it off future reports. Reports that checked it keep it.
      </p>
    </form>
  )
}

function AddStation({
  sectionKey,
  typeName,
  actions,
}: {
  sectionKey: string
  typeName: string
  actions: StationActions
}) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [location, setLocation] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const start = async () => {
    setOpen(true)
    setError(null)
    setLocation('')
    try {
      setName(await actions.suggestName(sectionKey))
    } catch {
      setName('')
    }
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (name.trim() === '') return
    setSaving(true)
    setError(null)
    try {
      await actions.add(sectionKey, name.trim(), location.trim() || null)
      setOpen(false)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That station did not save.')
    } finally {
      setSaving(false)
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => void start()}
        className="inline-flex min-h-[var(--control-md)] items-center justify-center gap-2 rounded-[14px] bg-white px-4 text-[13.5px] font-semibold text-ink-700 ring-1 ring-ink-300 ring-dashed ring-inset transition-colors hover:bg-ink-50"
      >
        <Icon name="plus" className="size-4" />
        Add a {typeName}
      </button>
    )
  }

  return (
    <form
      aria-label={`Add a ${typeName}`}
      onSubmit={(event) => void submit(event)}
      className="flex flex-col gap-2 rounded-[14px] bg-white p-[var(--card-pad)] ring-1 ring-ink-300 ring-inset"
    >
      <p className="text-[13px] font-semibold text-ink-900">A new {typeName} at this property</p>
      <div className="grid gap-2 sm:grid-cols-[120px_1fr]">
        <Input aria-label="Station name" value={name} onChange={(event) => setName(event.target.value)} />
        <Input
          aria-label="Where it is"
          placeholder="Where it is: kitchen, under the pass"
          value={location}
          onChange={(event) => setLocation(event.target.value)}
        />
      </div>
      {error ? <p className="text-xs text-red-700">{error}</p> : null}
      <div className="flex gap-2">
        <Button type="submit" size="sm" variant="primary" loading={saving}>
          Add station
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
